import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from "@angular/core";
import {
	AddressBookWalletAssetRipple,
	AddressBookWalletModel
} from "@modules/main/components/address-book-wallet/model/address-book-wallet.model";
import { CoreModule } from "@modules/core/core.module";
import { TranslationPipeModule } from "@modules/core/pipes/translation-pipe/translation-pipe.module";
import { ModalService } from "@core-services/modal.service";
import { take, withLatestFrom } from "rxjs/operators";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { Cryptocoin } from "@models";
import { CryptocoinService } from "@main-services/currency";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { SendingModalRemoteService } from "@modules/main/components/step-summary/sending-modal/services/sending-modal-remote.service";
import { AddressBookWalletFacade } from "@modules/main/components/address-book-wallet/services/address-book-wallet.facade";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { MultichainContactFacade } from "@modules/main/components/multichain-contact/services/multichain-contact.facade";

@Component({
	selector: "app-view-wallet-modal",
	imports: [CoreModule, TranslationPipeModule],
	providers: [MultichainContactFacade],
	templateUrl: "./address-book-view-wallet-modal.component.html",
	styleUrl: "./address-book-view-wallet-modal.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressBookViewWalletModalComponent implements OnInit {
	@Input() data: AddressBookWalletModel;
	protected readonly walletFacade = inject(AddressBookWalletFacade);
	protected readonly sendEntryPoint = inject(SendEntryPointService);

	public destinationTagLabel: string;

	constructor(
		private readonly modalService: ModalService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly coinService: CryptocoinService,
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly sendingModalRemote: SendingModalRemoteService,
		private readonly multichainService: MultichainContactFacade
	) {}

	ngOnInit(): void {
		this.initData();
	}

	private initData(): void {
		if (!this.data.wallet_host_name && this.data.wallet_host_type === "SELF_HOSTED") {
			this.data.wallet_host_name = this.translationService.translate("self_hosted_wallet");
		}
		if (this.data.wallet_destination_tag) {
			this.destinationTagLabel = this.getDestinationTagLabel();
		}
	}

	public close(): void {
		this.modalService.popAll();
	}

	public deleteWallet(): void {
		this.walletFacade.deleteWalletById(this.data.id);
	}

	public async send(): Promise<void> {
		//EDGE CASE FOR RIPPLE
		const assetName =
			this.data.asset.name === AddressBookWalletAssetRipple.RIPPLE
				? AddressBookWalletAssetRipple.XRP
				: this.data.asset.name;

		const coin: Cryptocoin = this.coinService.findByName(assetName);
		const status: WalletsBalanceStatus = await this.tradableWalletListService.getCoinWalletsBalances(coin.getId());
		if (status.balances.every((balance) => balance.operationalBalance <= 0) && status.hasUnconfirmedBalance) {
			this.infoService.setError(this.translationService.translate("recent_deposit_disclaimer"));
			return;
		}

		if (
			this.tradableWalletListService.getNonEmptyWallets(coin).length === 0 ||
			(status.balances.every((balance) => balance.operationalBalance <= 0) && !status.hasUnconfirmedBalance)
		) {
			this.infoService.setError(this.translationService.translate("cannot_send_because_no_funds_contacts"));
			return;
		}

		if (!coin.isWithdrawActive() || coin.isMaintenance()) {
			this.infoService.setError(this.translationService.translate("action_send_inactive_tooltip"));
			return;
		}

		const sendStatus = await this.sendEntryPoint.getSendStatusForCryptoCoin$(coin).pipe(take(1)).toPromise();
		const assetWallets: TradableWallet[] = this.tradableWalletListService.getWalletsForTradable(
			coin.getId().toString()
		);

		const sortFunction: (w1: TradableWallet, w2: TradableWallet) => number = (w1, w2) =>
			w2.getBalance() - w1.getBalance();
		const highestBalanceWallet = assetWallets.sort(sortFunction)[0];

		if (sendStatus.isDisabled) {
			if (sendStatus.message === "action_send_tooltip") {
				this.sendingModalRemote
					.fetchPriceLimits("external", highestBalanceWallet.getId())
					.pipe(take(1))
					.subscribe({
						next: (price) => {
							this.infoService.setError(
								this.translationService.translate(sendStatus.message, [
									price.getMinAmountExternal().tradableAmount() + " " + coin.getSymbol()
								])
							);
						},
						error: (error) => {
							this.infoService.setError(error);
						}
					});
				return;
			}
			this.infoService.setError(this.translationService.translate(sendStatus.message));
			return;
		}

		this.multichainService
			.getNetworkByCoinAndNetworkId(coin.getId(), this.data.network.coin_network_id)
			.pipe(
				take(1),
				withLatestFrom(this.walletFacade.getCoinByCryptocoinName(this.data.asset.name).pipe(take(1)))
			)
			.subscribe({
				next: ([detailedNetwork, paymentsCryptocoin]) => {
					this.walletFacade.validateWalletToSend(
						highestBalanceWallet.getId(),
						this.data,
						detailedNetwork,
						paymentsCryptocoin
					);
				}
			});
	}

	private getDestinationTagLabel(): string {
		switch (this.data.wallet_destination_tag_type) {
			case "message":
				return this.translationService.translate("message_tag");
			case "memo":
				return this.translationService.translate("stellar_memo");
			default: {
				return this.translationService.translate("destination_tag");
			}
		}
	}
}
